// Structured Data (JSON-LD) utilities for SEO

export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "BaileBelle",
    "description": "Premium Ballerina Dresses & Jewelry - Discover our exquisite collection of ballerina dresses and elegant jewelry. Premium quality, perfect fit, and timeless elegance for every dancer.",
    "url": "https://bailebelle.com",
    "logo": "https://bailebelle.com/logo512.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-555-0123",
      "contactType": "customer service",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://www.facebook.com/bailebelle",
      "https://www.instagram.com/bailebelle",
      "https://www.twitter.com/bailebelle",
      "https://www.pinterest.com/bailebelle"
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Dance Street",
      "addressLocality": "New York",
      "addressRegion": "NY",
      "postalCode": "10001",
      "addressCountry": "US"
    }
  };
};

export const generateWebsiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BaileBelle",
    "description": "Premium Ballerina Dresses & Jewelry",
    "url": "https://bailebelle.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://bailebelle.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BaileBelle",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bailebelle.com/logo512.png"
      }
    }
  };
};

export const generateProductSchema = (product) => {
  if (!product) return null;

  const baseUrl = "https://bailebelle.com";
  const productUrl = `${baseUrl}/products/${product.slug || product.id}`;
  const productImage = product.images?.[0]?.src || `${baseUrl}/placeholder-image.jpg`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.short_description || product.description || `Premium ${product.name} from BaileBelle`,
    "image": product.images?.map(img => img.src) || [productImage],
    "url": productUrl,
    "sku": product.sku || product.id.toString(),
    "brand": {
    },
    "shippingDetails": {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": "0",
        "currency": "USD"
      },
      "deliveryTime": {
        "@type": "ShippingDeliveryTime",
        "businessDays": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
        },
        "cutoffTime": "14:00",
        "handlingTime": {
          "@type": "QuantitativeValue",
          "minValue": 1,
          "maxValue": 2,
          "unitCode": "DAY"
        },
        "transitTime": {
          "@type": "QuantitativeValue",
          "minValue": 3,
          "maxValue": 5,
          "unitCode": "DAY"
        }
      }
    },
    "offers": {
      "@type": "Offer",
      "url": productUrl,
      "priceCurrency": "USD",
      "price": product.price || "0",
      "availability": product.stock_status === 'outofstock' ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
    }
  };


  // Add regular price if on sale
  if (product.sale_price && product.regular_price && product.sale_price < product.regular_price) {
    schema.offers.price = product.sale_price;
    schema.offers.priceSpecification = {
      "@type": "PriceSpecification",
      "price": product.sale_price,
      "priceCurrency": "USD"
    };
    schema.offers.priceSpecification.eligibleQuantity = {
      "@type": "QuantitativeValue",
      "minValue": 1
    };
  }

  // Add categories
  if (product.categories && product.categories.length > 0) {
    schema.category = product.categories.map(cat => cat.name).join(", ");
  }

  // Add weight and dimensions if available
  if (product.weight) {
    schema.weight = {
      "@type": "QuantitativeValue",
      "value": product.weight,
      "unitCode": "KGM"
    };
  }

  if (product.dimensions) {
    schema.dimensions = {
      "@type": "QuantitativeValue",
      "value": `${product.dimensions.length} x ${product.dimensions.width} x ${product.dimensions.height}`,
      "unitCode": "CMT"
    };
  }

  return schema;
};

export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

export const generateCollectionSchema = (collection) => {
  if (!collection) return null;

  const baseUrl = "https://bailebelle.com";

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": collection.name,
    "description": collection.description || `Browse our collection of ${collection.name}`,
    "url": `${baseUrl}/collections/${collection.slug}`,
    "mainEntity": {
      "@type": "ItemList",
      "name": collection.name,
      "description": collection.description || `Collection of ${collection.name}`,
      "numberOfItems": collection.count || 0
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Collections",
          "item": `${baseUrl}/collections`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": collection.name,
          "item": `${baseUrl}/collections/${collection.slug}`
        }
      ]
    }
  };
};

export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const generateLocalBusinessSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "BaileBelle",
    "description": "Premium Ballerina Dresses & Jewelry Store",
    "url": "https://bailebelle.com",
    "telephone": "+1-555-0123",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Dance Street",
      "addressLocality": "New York",
      "addressRegion": "NY",
      "postalCode": "10001",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "40.7128",
      "longitude": "-74.0060"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "priceRange": "$$",
    "paymentAccepted": ["Cash", "Credit Card", "PayPal"],
    "currenciesAccepted": "USD"
  };
};
